import React, { useState } from "react";

import { Close as CloseIcon } from "@mui/icons-material";
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Stack,
    Typography,
    useTheme,
} from "@mui/material";

import ClipLoader from "react-spinners/ClipLoader";

import {
    deleteDestination,
    deleteDestinationFromTracker,
} from "helpers/destinations/deleteDestination";

import SwalAlertComp from "components/SwalAlert";

import { Destination, Language, Tracker } from "../DestinationModal.types";
import DestinationIcon from "./DestinationIcon";

export type DeleteDestinationDialogMode = "tracker" | "standalone";

interface DeleteDestinationDialogProps {
    open: boolean;
    onClose: () => void;
    destination: Destination;
    tracker?: Tracker;
    language: Language;
    onDeleteSuccess: () => void;
    onRefresh?: () => void;
    /** Global destinations list uses standalone API; tracker detail uses remove-from-tracker. */
    mode?: DeleteDestinationDialogMode;
}

const DeleteDestinationDialog: React.FC<DeleteDestinationDialogProps> = ({
    open,
    onClose,
    destination,
    tracker,
    language,
    onDeleteSuccess,
    onRefresh,
    mode = "tracker",
}) => {
    const theme = useTheme();
    const isStandalone = mode === "standalone";
    const [isChecked, setIsChecked] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const handleClose = () => {
        if (!isDeleting) {
            setIsChecked(false);
            onClose();
        }
    };

    const handleDelete = async () => {
        if (!destination?.id) return;

        if (!isStandalone && !tracker?.tracker?.id) return;

        setIsDeleting(true);
        try {
            const response = isStandalone
                ? await deleteDestination(destination.id)
                : await deleteDestinationFromTracker(
                      tracker?.tracker?.id,
                      destination.id,
                      isChecked,
                  );

            if (response.status) {
                SwalAlertComp({
                    title: language?.delete_swal_modal?.success ?? "Success",
                    message: response.message,
                    icon: "success",
                    timer: 3000,
                });
                onDeleteSuccess();
                onRefresh?.();
                handleClose();
            } else {
                SwalAlertComp({
                    title: language?.delete_swal_modal?.failed ?? "Failed",
                    message: response.message,
                    icon: "error",
                });
            }
        } catch (error) {
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message: isStandalone
                    ? "An error occurred while deleting the destination"
                    : "An error occurred while removing the destination",
                icon: "error",
            });
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    pb: 1,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="h6" component="div">
                        {isStandalone
                            ? "Delete Destination"
                            : (language?.buttons?.remove_destination ??
                              "Remove Destination")}
                    </Typography>
                </Box>
                <IconButton
                    onClick={handleClose}
                    disabled={isDeleting}
                    size="small"
                    sx={{
                        color: theme.palette.text.secondary,
                        "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                        },
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 3 }}>
                <Box sx={{ mb: 3, mt: 3 }}>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        paragraph
                    >
                        {isStandalone
                            ? "This action will permanently delete the destination. This cannot be undone."
                            : "This action will remove the destination from your tracker. This cannot be undone."}
                    </Typography>
                </Box>

                <Stack spacing={3}>
                    <Box>
                        <Typography
                            variant="subtitle2"
                            gutterBottom
                            sx={{ fontWeight: 600 }}
                        >
                            Destination Information
                        </Typography>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                                p: 2,
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 1,
                            }}
                        >
                            <DestinationIcon
                                code={destination?.destination_type?.code}
                                size="medium"
                            />
                            <Box>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontWeight: 500,
                                    }}
                                >
                                    {destination?.destination_type?.name}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                        wordBreak: "break-word",
                                    }}
                                >
                                    {destination?.partner_reference_id ??
                                        destination?.name ??
                                        "—"}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    {!isStandalone && destination?.deletable && (
                        <Box>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={isChecked}
                                        onChange={(e) =>
                                            setIsChecked(e.target.checked)
                                        }
                                        disabled={isDeleting}
                                    />
                                }
                                label={
                                    <Box>
                                        <Typography
                                            variant="body2"
                                            sx={{ fontWeight: 500 }}
                                        >
                                            Delete Destination from everywhere
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            This will permanently remove the
                                            destination from your tracker and
                                            all associated data
                                        </Typography>
                                    </Box>
                                }
                            />
                        </Box>
                    )}
                </Stack>
            </DialogContent>

            <DialogActions
                sx={{ p: 3, gap: 1, justifyContent: "space-between" }}
            >
                <Button
                    onClick={handleClose}
                    disabled={isDeleting}
                    variant="outlined"
                >
                    {"Cancel"}
                </Button>

                <div className="position-relative overflow-hidden z-1">
                    {isDeleting && (
                        <div className="circle-loader max-height-45">
                            <ClipLoader color={"#000"} size={17} />
                        </div>
                    )}
                    <Button
                        onClick={handleDelete}
                        variant="contained"
                        color="error"
                    >
                        {isDeleting
                            ? isStandalone
                                ? "Deleting..."
                                : "Removing..."
                            : isStandalone
                              ? "Delete"
                              : (language?.buttons?.remove ?? "Remove")}
                    </Button>
                </div>
            </DialogActions>
        </Dialog>
    );
};

export default DeleteDestinationDialog;
